/***
 *
 *   PUBLIC LAYOUT
 *   The white-labelled shell for /project/:name. The project's own logo takes
 *   the header slot where the Valetify wordmark used to be, and the palette is
 *   the tenant's — see components/brand/brandScope.js.
 *
 *   The Sign in / Get started CTAs are gone on purpose. They pointed at the
 *   Valetify marketing site from a page dressed as someone else's, and the one
 *   affordance a returning visitor actually needs — "view tickets" — already
 *   lives inside the intake card itself.
 *
 *   What stays Valetify: a muted "Powered by Valetify" in the footer. It is the
 *   only attribution left, and it is also the thing that makes an abuse report
 *   about one of these pages actionable, so it is not optional.
 *
 *   The theme toggle stays too: it is a comfort control rather than branding,
 *   and the derived palette is built for both themes.
 *
 **********/

import { useParams } from 'react-router-dom';
import { BrandScope, Icon, Link, ProjectIdentityMark, Skeleton, useTheme } from 'components/lib';
import { useProjectBrand } from 'components/hooks/branding';
import { useTranslate } from 'app/translations';
import { rootPath } from 'utilities/routes';
import Style from './public.tailwind.js';

export function PublicLayout(props) {
    const { _t } = useTranslate();
    const { name } = useParams();
    const { isDark, toggleTheme } = useTheme();
    const { project, brandColor, isLoading, notFound } = useProjectBrand(name);

    // A neutral skeleton for the one round trip, rather than the Valetify
    // lockup. There is no server-rendered shell to brand, so branding always
    // arrives a beat late — but a page that starts blank and fills in reads as
    // loading, whereas one that flashes Valetify indigo and then swaps reads as
    // broken. Only the second is worth avoiding.
    //
    // A project that does not exist stays fully unbranded, so a mistyped URL
    // never produces a half-branded page.
    const identity =
        isLoading ? (
            <Skeleton className="h-8 w-32" />
        ) : notFound ? (
            <Link url={rootPath('/')} className={Style.logo}>
                <img src="/assets/icon.png" alt="" className={Style.logoMark} />
                Valetify
            </Link>
        ) : (
            <Link url={`/project/${name}`} className={Style.identity}>
                <ProjectIdentityMark
                    size="sm"
                    name={project.name}
                    filePath={project.file_path}
                    alt={project.name}
                />
                {!project.file_path && (
                    <span className={Style.identityName}>{project.name}</span>
                )}
            </Link>
        );

    return (
        <BrandScope color={brandColor} className={Style.scope}>
            <div className={Style.shell}>
                <header className={Style.header}>
                    {identity}

                    <div className={Style.actions}>
                        <button
                            type="button"
                            onClick={toggleTheme}
                            className={Style.theme}
                            title={_t(
                                isDark
                                    ? 'landing.nav.theme.toLight'
                                    : 'landing.nav.theme.toDark',
                            )}
                            aria-label={_t(
                                isDark
                                    ? 'landing.nav.theme.toLight'
                                    : 'landing.nav.theme.toDark',
                            )}>
                            <Icon image={isDark ? 'sun' : 'moon'} size={16} />
                        </button>
                    </div>
                </header>

                <main className={Style.main}>
                    <props.children {...props.data} />
                </main>

                <footer className={Style.footer}>
                    <span>
                        {`© ${new Date().getFullYear()} ${project?.name || 'Valetify'}.`}
                    </span>
                    <Link
                        url={rootPath('/')}
                        text={`${_t('poweredBy')} Valetify`}
                        className={Style.attribution}
                    />
                </footer>
            </div>
        </BrandScope>
    );
}
